import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, FileText, Upload, X, FileUp, Loader2, Edit2 } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
// Bundle the pdf.js worker through Vite so the browser gets a real worker
// instance instead of fetching a server-served module asset. That avoids MIME
// issues from reverse proxies that return text/plain for .mjs files.
import PdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?worker';
import { lettersApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { LetterDocument } from './LetterDocument';
import {
  DraftSignaturePlacement,
  LETTER_PAGE_HEIGHT_PX,
  LETTER_PAGE_WIDTH_PX,
  sanitizeDraftSignaturePlacement,
} from './signatureLayoutShared';

// Configure pdf.js worker from the Vite-bundled worker class above.
pdfjsLib.GlobalWorkerOptions.workerPort = new PdfWorker();

// ── Shared utilities (mirrored from CreateLetterPage) ────────────────────────

interface SignatoryOption {
  id: number;
  name: string;
  email: string;
  signer_name?: string;
  designation?: string;
  has_signature?: number | boolean;
}

interface LetterDetail {
  id: number;
  serial_number: number;
  letter_number: string;
  subject: string;
  letter_date: string;
  file_name?: string;
  department?: string;
  tags?: string[];
  assigned_signatory_user_id?: number | null;
  assigned_signatory_name?: string;
  approved_signature_html?: string | null;
  qr_code_data?: string | null;
  status: 'draft' | 'pending_approval' | 'approved' | 'rejected';
  body_html: string;
  letterhead_html: string;
  footer_html: string;
  updated_at?: string;
}

interface SignaturePlacementConfig {
  markAllPages: boolean;
  base: DraftSignaturePlacement;
  overrides: Record<string, DraftSignaturePlacement>;
  skippedPages?: number[];
}

const departments = ['General', 'Finance', 'Sales', 'Operations', 'Customer Excellence'];
const SIGNATURE_CONFIG_PREFIX = 'sepl-signature-config-';
const PDF_BODY_TARGET_WIDTH_PX = 682;
const PDF_BODY_TARGET_HEIGHT_PX = 850;
const PDF_RENDER_QUALITY_SCALE = 3;
const PDF_PAGE_IMAGE_MIME_TYPE = 'image/jpeg';
const PDF_PAGE_IMAGE_QUALITY = 0.92;

const defaultDraftSignaturePlacement: DraftSignaturePlacement = {
  pageIndex: 0,
  x: 56,
  y: 850,
  width: 168,
  height: 140,
};

const sanitizePlacement = (
  input?: Partial<DraftSignaturePlacement> | null,
): DraftSignaturePlacement =>
  sanitizeDraftSignaturePlacement(input, defaultDraftSignaturePlacement);

const defaultSignaturePlacementConfig: SignaturePlacementConfig = {
  markAllPages: false,
  base: defaultDraftSignaturePlacement,
  overrides: {},
  skippedPages: [],
};

const compactSignatureConfig = (config: SignaturePlacementConfig) => {
  const normalizedOverrides: Record<string, DraftSignaturePlacement> = {};
  Object.entries(config.overrides || {}).forEach(([key, value]) => {
    const page = String(Math.max(0, Number(key) || 0));
    normalizedOverrides[page] = sanitizePlacement(value);
  });
  return {
    markAllPages: !!config.markAllPages,
    base: sanitizePlacement(config.base),
    overrides: normalizedOverrides,
    skippedPages: (config.skippedPages || []).filter((n) => typeof n === 'number'),
  };
};

const encodeSignatureConfigToken = (config: SignaturePlacementConfig) =>
  encodeURIComponent(JSON.stringify(compactSignatureConfig(config)));

const decodeSignatureConfigToken = (token: string): SignaturePlacementConfig | null => {
  try {
    const parsed = JSON.parse(decodeURIComponent(token));
    const base = sanitizePlacement(parsed?.base);
    const overrides: Record<string, DraftSignaturePlacement> = {};
    Object.entries(parsed?.overrides || {}).forEach(([key, value]) => {
      overrides[String(Math.max(0, Number(key) || 0))] = sanitizePlacement(
        value as Partial<DraftSignaturePlacement>,
      );
    });
    const markAllPages = Boolean(parsed?.markAllPages);
    const rawSkippedPages = Array.isArray(parsed?.skippedPages)
      ? (parsed.skippedPages as number[]).filter((n: unknown) => typeof n === 'number')
      : [];
    return {
      markAllPages,
      base,
      overrides,
      // Honour skipped pages even when "Mark Sig on All Pages" is on: enabling
      // that toggle already clears stale skips at the source, so saved skips are
      // intentional per-page removals.
      skippedPages: rawSkippedPages,
    };
  } catch {
    return null;
  }
};

const extractSignaturePlacementFromBody = (
  html: string,
): { cleanHtml: string; config: SignaturePlacementConfig | null } => {
  if (!html) return { cleanHtml: html, config: null };
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const configMarker = doc.querySelector('[class*="sepl-signature-config-"]');
    let config: SignaturePlacementConfig | null = null;
    if (configMarker instanceof HTMLElement) {
      const match = configMarker.className.match(/sepl-signature-config-([^\s]+)/i);
      if (match?.[1]) config = decodeSignatureConfigToken(match[1]);
      configMarker.remove();
    }
    return { cleanHtml: doc.body.innerHTML || '<p></p>', config };
  } catch {
    return { cleanHtml: html, config: null };
  }
};

const injectSignaturePlacementIntoBody = (html: string, config: SignaturePlacementConfig) => {
  const base = html || '<p></p>';
  const withoutExisting = base.replace(
    new RegExp(
      `<span[^>]*class=["'][^"']*${SIGNATURE_CONFIG_PREFIX}[^"']*["'][^>]*><\\/span>`,
      'gi',
    ),
    '',
  );
  const markerClass = `${SIGNATURE_CONFIG_PREFIX}${encodeSignatureConfigToken(config)}`;
  return `${withoutExisting}<span class="${markerClass}"></span>`;
};

const normalizeLetterDate = (value: unknown, fallback: string) => {
  if (typeof value === 'string' && value.trim()) return value.slice(0, 10);
  return fallback;
};

const normalizeLetterTags = (value: unknown): string[] => {
  const source = Array.isArray(value) ? value : [];
  return Array.from(new Set(source.map((tag) => String(tag || '').trim()).filter(Boolean))).slice(
    0,
    20,
  );
};

const isSelfSignatoryOption = (
  option: SignatoryOption | null | undefined,
  user: { id?: string; email?: string } | null | undefined,
) => {
  if (!option || !user) return false;
  return (
    String(option.id) === String(user.id) ||
    (!!user.email &&
      String(option.email || '')
        .trim()
        .toLowerCase() ===
        String(user.email || '')
          .trim()
          .toLowerCase())
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

export function UploadPdfLetterPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const previewRef = useRef<HTMLDivElement>(null);
  const exportRenderRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const letterId = searchParams.get('letterId');
  const isEditingExistingLetter = Boolean(letterId);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subject, setSubject] = useState('Untitled PDF Letter');
  const [fileName, setFileName] = useState('');
  const [letterDate, setLetterDate] = useState(new Date().toISOString().slice(0, 10));
  const [letterheadHtml, setLetterheadHtml] = useState('');
  const [footerHtml, setFooterHtml] = useState('');
  const [department, setDepartment] = useState('General');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [assignedSignatoryUserId, setAssignedSignatoryUserId] = useState('');
  const [signatoryOptions, setSignatoryOptions] = useState<SignatoryOption[]>([]);
  const [currentLetter, setCurrentLetter] = useState<LetterDetail | null>(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [signatureConfig, setSignatureConfig] = useState<SignaturePlacementConfig>(
    defaultSignaturePlacementConfig,
  );
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsRequiredFlow, setDetailsRequiredFlow] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const latestDraftSnapshotRef = useRef('');

  // PDF-specific state
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null);
  const [pdfBodyHtml, setPdfBodyHtml] = useState<string>('<p></p>');
  const [pdfRendering, setPdfRendering] = useState(false);
  const [pdfRenderError, setPdfRenderError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const canEdit = useMemo(() => {
    const permissions = user?.permissions;
    return permissions?.can_view_letters || permissions?.is_admin;
  }, [user]);

  const hasRequiredDetails = useMemo(
    () =>
      !!String(fileName || '').trim() &&
      !!String(letterDate || '').trim() &&
      !!String(department || '').trim(),
    [department, fileName, letterDate],
  );

  const bodyHtmlForSave = pdfBodyHtml?.trim() ? pdfBodyHtml : '<p></p>';

  const buildDraftSnapshot = useCallback(() => {
    return JSON.stringify({
      subject,
      fileName,
      letterDate,
      department,
      tags,
      assignedSignatoryUserId: assignedSignatoryUserId || null,
      bodyHtml: injectSignaturePlacementIntoBody(bodyHtmlForSave, signatureConfig),
      status: 'draft',
    });
  }, [
    assignedSignatoryUserId,
    bodyHtmlForSave,
    department,
    fileName,
    letterDate,
    signatureConfig,
    subject,
    tags,
  ]);

  useEffect(() => {
    latestDraftSnapshotRef.current = buildDraftSnapshot();
  }, [buildDraftSnapshot]);

  const isSelfSelected = useMemo(() => {
    const selected = signatoryOptions.find((o) => String(o.id) === assignedSignatoryUserId) || null;
    return Boolean(isSelfSignatoryOption(selected, user));
  }, [assignedSignatoryUserId, signatoryOptions, user]);

  // Render each PDF page to a canvas image and build bodyHtml
  const renderPdfToImages = useCallback(async (arrayBuffer: ArrayBuffer) => {
    try {
      setPdfRendering(true);
      setPdfRenderError(null);
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const pageAwareQualityScale =
        pdf.numPages > 10 ? 2 : pdf.numPages > 5 ? 2.5 : PDF_RENDER_QUALITY_SCALE;
      const renderedPages: Array<{ src: string; width: number; height: number }> = [];

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        // Scale to fit inside letter body area (header/footer remain visible)
        const viewport = page.getViewport({ scale: 1 });
        const widthScale = PDF_BODY_TARGET_WIDTH_PX / viewport.width;
        const heightScale = PDF_BODY_TARGET_HEIGHT_PX / viewport.height;
        const displayScale = Math.min(widthScale, heightScale);
        const renderScale = displayScale * pageAwareQualityScale;
        const scaledViewport = page.getViewport({ scale: renderScale });

        const canvas = document.createElement('canvas');
        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;
        const ctx = canvas.getContext('2d')!;
        await page.render({ canvasContext: ctx, viewport: scaledViewport, canvas }).promise;
        renderedPages.push({
          src: canvas.toDataURL(PDF_PAGE_IMAGE_MIME_TYPE, PDF_PAGE_IMAGE_QUALITY),
          width: Math.round(scaledViewport.width / pageAwareQualityScale),
          height: Math.round(scaledViewport.height / pageAwareQualityScale),
        });
      }

      // Build body HTML: each PDF page as an image block
      const html = renderedPages
        .map(
          (pageData, i) =>
            `<div class="sepl-pdf-page">` +
            `<img class="sepl-pdf-page-image" src="${pageData.src}" width="${pageData.width}" height="${pageData.height}" alt="PDF page ${i + 1}" />` +
            `</div>`,
        )
        .join('');
      setPdfBodyHtml(html || '<p></p>');
      setDirty(true);
    } catch (err) {
      console.error('PDF render error:', err);
      setPdfRenderError(
        'Failed to render PDF preview. Showing the original PDF until the preview is ready again.',
      );
      setPdfBodyHtml('<p style="color:red;">Failed to render PDF preview.</p>');
      setDirty(true);
    } finally {
      setPdfRendering(false);
    }
  }, []);

  // Clean up object URL on unmount
  useEffect(() => {
    return () => {
      if (pdfObjectUrl) URL.revokeObjectURL(pdfObjectUrl);
    };
  }, [pdfObjectUrl]);

  const handlePdfSelect = useCallback(
    (file: File) => {
      if (file.type !== 'application/pdf') {
        alert('Please select a PDF file.');
        return;
      }
      if (pdfObjectUrl) URL.revokeObjectURL(pdfObjectUrl);
      const url = URL.createObjectURL(file);
      setPdfFile(file);
      setPdfObjectUrl(url);
      setPdfRenderError(null);
      setDirty(true);
      // Auto-fill file name from PDF filename if not already set
      if (!fileName) {
        setFileName(file.name.replace(/\.pdf$/i, ''));
        setDirty(true);
      }
      if (subject === 'Untitled PDF Letter') {
        setSubject(file.name.replace(/\.pdf$/i, ''));
        setDirty(true);
      }
      // Render PDF pages → images for preview
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result instanceof ArrayBuffer) {
          renderPdfToImages(e.target.result);
        }
      };
      reader.readAsArrayBuffer(file);
    },
    [pdfObjectUrl, fileName, subject, renderPdfToImages],
  );

  const removePdf = useCallback(() => {
    if (pdfObjectUrl) URL.revokeObjectURL(pdfObjectUrl);
    setPdfFile(null);
    setPdfObjectUrl(null);
    setPdfBodyHtml('<p></p>');
    setPdfRenderError(null);
    setDirty(true);
  }, [pdfObjectUrl]);

  // Read the latest user without making the loader depend on the object's
  // identity: a session refresh must never reload the letter being edited.
  const userRef = useRef(user);
  userRef.current = user;

  const loadLetterEditor = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError('');
      setAccessDenied(false);
      const [templateRes, signatoryRes] = await Promise.all([
        lettersApi.getTemplate(),
        lettersApi.listSignatoryOptions(),
      ]);
      setLetterheadHtml(templateRes.data.letterheadHtml || '');
      setFooterHtml(templateRes.data.footerHtml || '');
      setSignatoryOptions(signatoryRes.data || []);

      if (letterId) {
        try {
          const letterRes = await lettersApi.get(letterId);
          const letter = letterRes?.data || {};
          setCurrentLetter(letter);
          setSubject(letter.subject || 'Untitled PDF Letter');
          setFileName(letter.file_name || '');
          setLetterDate(
            normalizeLetterDate(letter.letter_date, new Date().toISOString().slice(0, 10)),
          );
          setDepartment(letter.department || 'General');
          setTags(normalizeLetterTags(letter.tags));
          setAssignedSignatoryUserId(
            letter.assigned_signatory_user_id ? String(letter.assigned_signatory_user_id) : '',
          );
          const parsed = extractSignaturePlacementFromBody(letter.body_html || '');
          if (parsed.config) setSignatureConfig(parsed.config);
          setPdfBodyHtml(parsed.cleanHtml || '<p></p>');
          setLetterheadHtml(letter.letterhead_html || templateRes.data.letterheadHtml || '');
          setFooterHtml(letter.footer_html || templateRes.data.footerHtml || '');
          setShowDetailsModal(false);
          setDetailsRequiredFlow(false);
        } catch (err: any) {
          if (err?.response?.status === 404) {
            const params = new URLSearchParams(searchParams);
            params.set('page', 'letters-upload-pdf');
            params.delete('letterId');
            setSearchParams(params);
            return;
          }
          throw err;
        }
      } else {
        const today = new Date().toISOString().slice(0, 10);
        setSubject('Untitled PDF Letter');
        setFileName('');
        setLetterDate(today);
        setDepartment('General');
        setTags([]);
        setAssignedSignatoryUserId('');
        setSignatureConfig(defaultSignaturePlacementConfig);
        // Auto-select self signatory if available
        const selfOption = (signatoryRes.data || []).find(
          (option: SignatoryOption) =>
            isSelfSignatoryOption(option, userRef.current) &&
            (option.has_signature === true || option.has_signature === 1),
        );
        if (selfOption) setAssignedSignatoryUserId(String(selfOption.id));
        setShowDetailsModal(true);
        setDetailsRequiredFlow(true);
      }
    } catch (err: any) {
      setAccessDenied(err?.response?.status === 403);
      setLoadError(err?.response?.data?.error || err?.message || 'Failed to load editor.');
    } finally {
      setLoading(false);
      setIsInitialized(true);
    }
  }, [letterId, searchParams, setSearchParams, user?.id]);

  useEffect(() => {
    loadLetterEditor();
  }, [loadLetterEditor]);

  const updateParams = useCallback(
    (page: string, extra: Record<string, string> = {}) => {
      const params = new URLSearchParams();
      params.set('page', page);
      Object.entries(extra).forEach(([key, value]) => params.set(key, value));
      setSearchParams(params);
    },
    [setSearchParams],
  );

  const hasRenderedPdfPages = /sepl-pdf-page/.test(pdfBodyHtml);

  const handleOpenDetailsEditor = () => {
    setDetailsError('');
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    if (detailsRequiredFlow && !hasRequiredDetails) {
      updateParams('letters-create');
      return;
    }
    setDetailsError('');
    setShowDetailsModal(false);
  };

  const handleConfirmDetails = () => {
    if (!hasRequiredDetails) {
      setDetailsError('File Name, Date and Department are required.');
      return;
    }
    const trimmedFileName = String(fileName || '').trim();
    const trimmedSubject = String(subject || '').trim();
    if (trimmedFileName && (!trimmedSubject || trimmedSubject === 'Untitled PDF Letter')) {
      setSubject(trimmedFileName);
    }
    setDetailsError('');
    setShowDetailsModal(false);
    setDetailsRequiredFlow(false);
    setDirty(true);
  };

  const handleSave = useCallback(
    async (status: 'draft' | 'pending_approval', isAutoSave = false) => {
      const autosaveSnapshotBeforeRequest = isAutoSave ? latestDraftSnapshotRef.current : '';
      try {
        if (!canEdit) return;
        setSaving(true);
        setSaveError('');

        // Never persist the empty placeholder body while a PDF is still
        // rasterizing (or failed to render). The autosave timer fires ~1.5s
        // after the file is picked — before the async page-image render
        // finishes — so without this guard the draft saves empty and the
        // uploaded PDF is lost. Only save once the body has real page images.
        if (pdfFile && !/sepl-pdf-page/.test(bodyHtmlForSave)) {
          if (!isAutoSave) {
            setSaveError(
              pdfRendering
                ? 'The PDF is still rendering — please wait a moment, then save.'
                : 'Please upload a PDF before saving.',
            );
          }
          return;
        }

        if (status === 'pending_approval' && !assignedSignatoryUserId) {
          setSaveError('Please select an authorized signatory.');
          setSaving(false);
          return;
        }

        if (detailsRequiredFlow && !hasRequiredDetails) {
          setSaveError('Please complete mandatory basic details before saving.');
          setSaving(false);
          setShowDetailsModal(true);
          setDetailsRequiredFlow(true);
          return;
        }

        const payload = {
          subject,
          fileName,
          letterDate,
          department,
          tags,
          assignedSignatoryUserId: assignedSignatoryUserId || null,
          bodyHtml: injectSignaturePlacementIntoBody(bodyHtmlForSave, signatureConfig),
          status,
          isAutoSave,
        };

        const response = currentLetter?.id
          ? await lettersApi.update(currentLetter.id, payload)
          : await lettersApi.create(payload);

        const nextLetter = response.data;
        setCurrentLetter(nextLetter);
        setSubject(nextLetter.subject || subject);
        setFileName(nextLetter.file_name || fileName);
        setDepartment(nextLetter.department || department);
        setTags(normalizeLetterTags(nextLetter.tags));
        setAssignedSignatoryUserId(
          nextLetter.assigned_signatory_user_id
            ? String(nextLetter.assigned_signatory_user_id)
            : '',
        );
        setLetterDate(normalizeLetterDate(nextLetter.letter_date, letterDate));
        setLetterheadHtml(nextLetter.letterhead_html || letterheadHtml);
        setFooterHtml(nextLetter.footer_html || footerHtml);
        if (isAutoSave) {
          const hasNewerUnsavedChanges =
            latestDraftSnapshotRef.current !== autosaveSnapshotBeforeRequest;
          setDirty(hasNewerUnsavedChanges);
        } else {
          setDirty(false);
        }

        if (!isAutoSave) {
          setSaveMessage(
            status === 'pending_approval' ? '✓ Submitted for signature' : '✓ Draft saved',
          );
          window.setTimeout(() => setSaveMessage(''), 2500);
        }
        if (isEditingExistingLetter && (!letterId || String(letterId) !== String(nextLetter.id))) {
          updateParams('letters-upload-pdf', { letterId: String(nextLetter.id) });
        }
      } catch (err: any) {
        const httpStatus = err?.response?.status;
        const serverMsg = err?.response?.data?.error;
        let msg = 'Save failed. Please try again.';
        if (httpStatus === 413)
          msg = 'Uploaded PDF content is too large. Please use a smaller PDF or fewer pages.';
        else if (httpStatus === 403) msg = 'You do not have permission to save letters.';
        else if (httpStatus === 401) msg = 'Session expired. Please log in again.';
        else if (serverMsg) msg = serverMsg;
        setSaveError(msg);
        window.setTimeout(() => setSaveError(''), 5000);
      } finally {
        setSaving(false);
      }
    },
    [
      assignedSignatoryUserId,
      bodyHtmlForSave,
      canEdit,
      currentLetter?.id,
      department,
      fileName,
      footerHtml,
      isEditingExistingLetter,
      letterDate,
      letterId,
      letterheadHtml,
      detailsRequiredFlow,
      hasRequiredDetails,
      signatureConfig,
      subject,
      tags,
      updateParams,
      pdfFile,
      pdfRendering,
    ],
  );

  // Auto-save
  useEffect(() => {
    if (!isInitialized || !dirty || !canEdit) return;
    // Don't autosave while the PDF is still rasterizing — the body is still the
    // empty placeholder, so saving now would persist an empty draft and lose
    // the uploaded PDF.
    if (pdfRendering) return;
    const timer = window.setTimeout(() => handleSave('draft', true), 1500);
    return () => window.clearTimeout(timer);
  }, [subject, letterDate, dirty, isInitialized, canEdit, handleSave, pdfRendering]);

  const handleSubmitOrSign = useCallback(async () => {
    if (detailsRequiredFlow && !hasRequiredDetails) {
      setSaveError('Please complete mandatory basic details before submitting.');
      setShowDetailsModal(true);
      setDetailsRequiredFlow(true);
      return;
    }

    if (!isSelfSelected) {
      await handleSave('pending_approval');
      return;
    }
    try {
      if (!canEdit || !assignedSignatoryUserId) {
        setSaveError('Please select an authorized signatory.');
        return;
      }
      setSaving(true);
      setSaveError('');

      const payload = {
        subject,
        fileName,
        letterDate,
        department,
        tags,
        assignedSignatoryUserId: assignedSignatoryUserId || null,
        bodyHtml: injectSignaturePlacementIntoBody(bodyHtmlForSave, signatureConfig),
        status: 'pending_approval' as const,
        isAutoSave: false,
      };

      const response = currentLetter?.id
        ? await lettersApi.update(currentLetter.id, payload)
        : await lettersApi.create(payload);

      const nextLetter = response.data;
      await lettersApi.approve(nextLetter.id);

      setSaveMessage('✓ Letter signed successfully');
      window.setTimeout(() => setSaveMessage(''), 2500);
      setDirty(false);
      updateParams('letters-view', { letterId: String(nextLetter.id) });
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Sign failed. Please try again.';
      setSaveError(msg);
      window.setTimeout(() => setSaveError(''), 5000);
    } finally {
      setSaving(false);
    }
  }, [
    assignedSignatoryUserId,
    bodyHtmlForSave,
    canEdit,
    currentLetter?.id,
    department,
    fileName,
    handleSave,
    detailsRequiredFlow,
    hasRequiredDetails,
    isSelfSelected,
    letterDate,
    signatureConfig,
    subject,
    tags,
    updateParams,
  ]);

  const downloadPdf = async () => {
    const sourceEl = exportRenderRef.current || previewRef.current;
    if (!sourceEl) return;
    const pageElements = Array.from(
      sourceEl.querySelectorAll('.sepl-letter-page'),
    ) as HTMLElement[];
    if (!pageElements.length) return;

    const [{ jsPDF }, html2canvasModule] = await Promise.all([
      import('jspdf'),
      import('html2canvas'),
    ]);
    const html2canvas = html2canvasModule.default;

    const MAX_PDF_SIZE_BYTES = 2 * 1024 * 1024;

    const buildPdfBlob = async (scale: number, imageQuality: number) => {
      const pdf = new jsPDF({
        unit: 'px',
        format: [794, 1122],
        orientation: 'portrait',
        compress: true,
      });

      for (let i = 0; i < pageElements.length; i++) {
        const canvas = await html2canvas(pageElements[i], {
          scale,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: LETTER_PAGE_WIDTH_PX,
          height: LETTER_PAGE_HEIGHT_PX,
          windowWidth: LETTER_PAGE_WIDTH_PX,
          windowHeight: LETTER_PAGE_HEIGHT_PX,
          scrollX: 0,
          scrollY: 0,
        });

        if (i > 0) pdf.addPage([794, 1122], 'portrait');
        pdf.addImage(
          canvas.toDataURL('image/jpeg', imageQuality),
          'JPEG',
          0,
          0,
          794,
          1122,
          undefined,
          'MEDIUM',
        );
      }

      return { blob: pdf.output('blob') };
    };

    const compressionAttempts = [
      { scale: 1.8, quality: 0.88 },
      { scale: 1.6, quality: 0.84 },
      { scale: 1.4, quality: 0.8 },
      { scale: 1.25, quality: 0.76 },
      { scale: 1.1, quality: 0.72 },
      { scale: 1, quality: 0.68 },
      { scale: 0.9, quality: 0.62 },
      { scale: 0.82, quality: 0.58 },
      { scale: 0.75, quality: 0.54 },
    ];

    let selectedBlob: Blob | null = null;

    for (const attempt of compressionAttempts) {
      const { blob } = await buildPdfBlob(attempt.scale, attempt.quality);
      selectedBlob = blob;
      if (blob.size <= MAX_PDF_SIZE_BYTES) {
        break;
      }
    }

    if (!selectedBlob) return;

    const label = currentLetter?.letter_number || fileName || subject;
    const url = URL.createObjectURL(selectedBlob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${label}.pdf`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="p-8 text-gray-600">Loading…</div>;
  if (loadError) {
    if (accessDenied) {
      return (
        <div className="p-8">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center max-w-lg mx-auto">
            <p className="text-amber-800 font-semibold mb-2">You can&apos;t view this letter</p>
            <p className="text-amber-700 text-sm mb-4">{loadError}</p>
            <button
              onClick={() => window.history.back()}
              className="px-4 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 text-sm"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center max-w-lg mx-auto">
          <p className="text-red-700 font-semibold mb-2">Failed to load</p>
          <p className="text-red-600 text-sm mb-4">{loadError}</p>
          <button
            onClick={loadLetterEditor}
            className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 text-sm"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* ── Top header bar (same structure as CreateLetterPage) ── */}
      <div className="bg-white border-b border-gray-200 px-3 md:px-6 py-3 flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap items-end gap-3 flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <div className="text-xs text-gray-500 uppercase tracking-wide">File Name</div>
            <div className="text-sm font-semibold text-gray-900 truncate max-w-[320px]">
              {fileName || 'Untitled PDF Letter'}
            </div>
            {canEdit && (
              <button
                type="button"
                onClick={handleOpenDetailsEditor}
                className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-violet-200 text-violet-700 hover:bg-violet-50"
                title="Edit details"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            )}
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 uppercase tracking-wide whitespace-nowrap">
                Signatory
              </label>
              <select
                value={assignedSignatoryUserId}
                onChange={(e) => {
                  setAssignedSignatoryUserId(e.target.value);
                  setDirty(true);
                }}
                className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm bg-white min-w-[160px]"
              >
                <option value="">Select signatory</option>
                {signatoryOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {isSelfSignatoryOption(option, user)
                      ? 'Self'
                      : `${option.signer_name || option.name}${option.designation ? ` · ${option.designation}` : ''}`}
                  </option>
                ))}
              </select>
            </div>
          )}
          {!canEdit && assignedSignatoryUserId && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 uppercase tracking-wide">Signatory</span>
              <span className="text-sm font-medium text-gray-800 bg-violet-50 border border-violet-200 rounded-lg px-2.5 py-1">
                {signatoryOptions.find((o) => String(o.id) === String(assignedSignatoryUserId))
                  ? isSelfSignatoryOption(
                      signatoryOptions.find(
                        (o) => String(o.id) === String(assignedSignatoryUserId),
                      )!,
                      user,
                    )
                    ? 'Self'
                    : `${signatoryOptions.find((o) => String(o.id) === String(assignedSignatoryUserId))!.signer_name || signatoryOptions.find((o) => String(o.id) === String(assignedSignatoryUserId))!.name}`
                  : ''}
              </span>
            </div>
          )}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs text-gray-500 uppercase tracking-wide">Tags</span>
            {tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 bg-violet-100 text-violet-800 text-xs font-medium rounded-full px-2.5 py-0.5"
              >
                {tag}
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => setTags((prev) => prev.filter((t) => t !== tag))}
                    className="hover:text-violet-600 ml-0.5"
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
            {canEdit && (
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ',') && tagInput.trim()) {
                    e.preventDefault();
                    const t = tagInput.trim().replace(/,$/, '');
                    if (t && !tags.includes(t)) setTags((prev) => [...prev, t]);
                    setTagInput('');
                  }
                }}
                placeholder="Add tag…"
                className="text-xs border border-gray-300 rounded-full px-2.5 py-0.5 w-24 focus:outline-none focus:ring-1 focus:ring-violet-300"
              />
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto flex-shrink-0">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg px-3 py-1.5">
            <input
              type="checkbox"
              checked={signatureConfig.markAllPages}
              disabled={!canEdit}
              onChange={(e) => {
                if (!canEdit) return;
                const checked = e.target.checked;
                setSignatureConfig((prev) => ({
                  ...prev,
                  markAllPages: checked,
                  base: { ...prev.base, pageIndex: 0 },
                  // Enabling "all pages" must clear any pages previously removed
                  // via the per-page ✕ button; otherwise the signature stays
                  // missing on those pages even though "all pages" is checked.
                  skippedPages: checked ? [] : prev.skippedPages,
                }));
                setDirty(true);
              }}
            />
            <span className="text-xs whitespace-nowrap">Mark Sig on All Pages</span>
          </label>

          {saveMessage && (
            <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-2.5 py-1.5 rounded-lg font-medium">
              {saveMessage}
            </span>
          )}
          {saveError && (
            <span className="text-xs text-red-700 bg-red-50 border border-red-200 px-2.5 py-1.5 rounded-lg font-medium">
              ⚠ {saveError}
            </span>
          )}
          {!saveMessage &&
            !saveError &&
            (saving ? (
              <span className="text-xs text-violet-600 bg-violet-50 border border-violet-200 px-2.5 py-1.5 rounded-lg font-medium animate-pulse">
                Saving…
              </span>
            ) : (
              <span className="text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 rounded-lg">
                Auto Saved {currentLetter?.id ? '✓' : ''}
              </span>
            ))}

          <button
            onClick={handleSubmitOrSign}
            disabled={!canEdit || saving || !assignedSignatoryUserId}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium disabled:opacity-60"
          >
            <FileText className="w-4 h-4" />{' '}
            {isSelfSelected ? 'Sign Letter' : 'Submit for Signature'}
          </button>
          <button
            onClick={downloadPdf}
            disabled={!currentLetter?.id}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-violet-200 bg-violet-50 text-violet-700 text-sm font-medium disabled:opacity-60"
          >
            <Download className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      {showDetailsModal && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Enter Basic Details</h3>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">
                  File Name *
                </label>
                <input
                  type="text"
                  value={fileName}
                  disabled={!canEdit}
                  onChange={(e) => {
                    setFileName(e.target.value);
                    setDirty(true);
                  }}
                  placeholder="e.g., Proposal_2026"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">
                  Date *
                </label>
                <input
                  type="date"
                  value={letterDate}
                  disabled={!canEdit}
                  onChange={(e) => {
                    setLetterDate(e.target.value);
                    setDirty(true);
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 block">
                  Department *
                </label>
                <select
                  value={department}
                  disabled={!canEdit}
                  onChange={(e) => {
                    setDepartment(e.target.value);
                    setDirty(true);
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
                >
                  {departments.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {detailsError ? (
              <div className="px-5 pb-2 text-sm text-red-600">⚠ {detailsError}</div>
            ) : null}
            <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleCloseDetailsModal}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
              >
                {detailsRequiredFlow ? 'Back' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={handleConfirmDetails}
                className="px-4 py-2 rounded-lg bg-violet-600 text-white hover:bg-violet-700 text-sm font-medium"
              >
                Create Letter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Main content: two-column layout ── */}
      <div className="p-6 md:p-8 max-w-[1600px] mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        {/* LEFT: PDF Upload / Viewer */}
        <div className="space-y-4 min-w-0">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <FileUp className="w-5 h-5 text-violet-600" />
              PDF Source Document
            </h2>
            {pdfFile && (
              <button
                onClick={removePdf}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition"
              >
                <X className="w-4 h-4" /> Remove PDF
              </button>
            )}
          </div>

          {!pdfFile ? (
            /* Drop zone */
            <div
              className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition ${
                isDragOver
                  ? 'border-violet-500 bg-violet-50'
                  : 'border-gray-300 hover:border-violet-400 hover:bg-violet-50/40'
              }`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragOver(false);
                const file = e.dataTransfer.files?.[0];
                if (file) handlePdfSelect(file);
              }}
            >
              <Upload className="w-12 h-12 text-violet-400 mx-auto mb-3" />
              <p className="text-gray-700 font-semibold text-lg mb-1">Drop your PDF here</p>
              <p className="text-gray-500 text-sm">or click to browse files</p>
              <p className="text-gray-400 text-xs mt-3">Only PDF files are supported</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handlePdfSelect(file);
                }}
              />
            </div>
          ) : (
            /* PDF Viewer */
            <div className="rounded-xl border border-gray-200 overflow-hidden shadow-sm">
              <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 truncate">{pdfFile.name}</span>
                <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                  {(pdfFile.size / 1024 / 1024).toFixed(2)} MB
                </span>
              </div>
              <iframe
                src={pdfObjectUrl!}
                title="PDF Preview"
                className="w-full"
                style={{ height: '800px', border: 'none' }}
              />
            </div>
          )}

          {/* Info note */}
          <div className="rounded-lg bg-violet-50 border border-violet-200 px-4 py-3 text-sm text-violet-700">
            <p className="font-semibold mb-1">How this works</p>
            <ul className="list-disc list-inside space-y-1 text-violet-600 text-xs">
              <li>Upload the source PDF for reference — it is displayed here for context only.</li>
              <li>
                The right-hand preview shows the official letter with your letterhead, reference ID,
                and signature.
              </li>
              <li>Submit for signature to get an auto-generated Reference ID (letter number).</li>
              <li>Use the PDF download to export the official letter header/signature page.</li>
            </ul>
          </div>
        </div>

        {/* RIGHT: Letter Preview */}
        <div className="space-y-4 min-w-0">
          {pdfRenderError ? (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {pdfRenderError}
            </div>
          ) : pdfFile && (!hasRenderedPdfPages || pdfRendering) ? (
            <div className="rounded-xl border border-gray-200 overflow-hidden shadow-sm bg-white">
              <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 truncate">{pdfFile.name}</span>
                <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
                  {(pdfFile.size / 1024 / 1024).toFixed(2)} MB
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-violet-600 bg-violet-50 border-b border-violet-200 px-4 py-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Rendering PDF pages into preview…
              </div>
              <iframe
                src={pdfObjectUrl!}
                title="PDF Preview"
                className="w-full"
                style={{ height: '800px', border: 'none' }}
              />
            </div>
          ) : null}
          {pdfRendering && !pdfFile && (
            <div className="flex items-center gap-2 text-sm text-violet-600 bg-violet-50 border border-violet-200 rounded-lg px-4 py-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Rendering PDF pages into preview…
            </div>
          )}
          <LetterDocument
            ref={previewRef}
            subject={subject}
            letterDate={letterDate}
            serialNumber={currentLetter?.serial_number}
            letterNumber={currentLetter?.letter_number}
            bodyHtml={pdfBodyHtml}
            letterheadHtml={letterheadHtml}
            footerHtml={footerHtml}
            approvedSignatureHtml={currentLetter?.approved_signature_html}
            qrCodeData={currentLetter?.qr_code_data}
            signaturePlacementConfig={signatureConfig}
            enableDraftSignatureDrag={!!canEdit}
            showPrintBoundaryOverlay={false}
            onRemoveSignaturePage={(pageIndex) => {
              setSignatureConfig((prev) => ({
                ...prev,
                skippedPages: [...new Set([...(prev.skippedPages || []), pageIndex])],
              }));
              setDirty(true);
            }}
            onDraftSignaturePlacementChange={(next) => {
              setSignatureConfig((prev) => {
                if (prev.markAllPages) {
                  if (next.pageIndex === 0) return { ...prev, base: { ...next, pageIndex: 0 } };
                  return {
                    ...prev,
                    overrides: { ...(prev.overrides || {}), [String(next.pageIndex)]: next },
                  };
                }
                return { ...prev, base: next };
              });
              setDirty(true);
            }}
            fitViewport
          />
          <p className="text-xs text-gray-400 text-center">
            Drag the signature box to reposition · Reference ID auto-generated on approval
          </p>
        </div>
      </div>

      <div
        style={{
          position: 'fixed',
          left: '-9999px',
          top: 0,
          width: '794px',
          pointerEvents: 'none',
          opacity: 0,
          zIndex: -1,
        }}
        aria-hidden="true"
      >
        <div ref={exportRenderRef}>
          <LetterDocument
            subject={subject}
            letterDate={letterDate}
            serialNumber={currentLetter?.serial_number}
            letterNumber={currentLetter?.letter_number}
            bodyHtml={pdfBodyHtml}
            letterheadHtml={letterheadHtml}
            footerHtml={footerHtml}
            approvedSignatureHtml={currentLetter?.approved_signature_html}
            qrCodeData={currentLetter?.qr_code_data}
            signaturePlacementConfig={signatureConfig}
            exportMode
          />
        </div>
      </div>
    </div>
  );
}
